import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { fetchSession } from '../api/endpoints';
import { formatDateTime, formatUZS } from '../lib/format';
import { mediaUrl } from '../lib/media';
import { useUserStore } from '../store/userStore';
import TopBar from '../components/TopBar';
import Splash from '../components/Splash';

export default function SessionDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const lang = useUserStore((s) => s.lang);

  const { data, isLoading } = useQuery({
    queryKey: ['session', id],
    queryFn: () => fetchSession(id!),
    enabled: !!id,
  });

  if (isLoading || !data) return <Splash />;

  return (
    <div className="min-h-screen pb-32">
      <TopBar title={data.title} />
      <div className="aspect-[3/4] bg-bg-elev relative">
        {data.poster_url && <img src={mediaUrl(data.poster_url)} className="w-full h-full object-cover" alt={data.title} />}
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-bg to-transparent" />
      </div>

      <div className="px-4 -mt-6 relative">
        <h1 className="text-2xl font-bold mb-3">{data.title}</h1>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <button
            className="card p-3 text-left active:opacity-80 transition"
            onClick={() => data.cinema_id && navigate(`/cinema/${data.cinema_id}`)}
          >
            <div className="text-xs text-muted">{t('session.cinema')} ›</div>
            <div className="text-sm font-medium mt-0.5">{data.cinema_name}</div>
            {data.address && <div className="text-xs text-muted mt-1">{data.address}</div>}
          </button>
          <div className="card p-3">
            <div className="text-xs text-muted">{formatDateTime(data.starts_at, lang)}</div>
            <div className="text-sm font-medium mt-0.5">{t('session.duration')}: {data.duration_min} {t('common.min')}</div>
          </div>
        </div>

        {data.description && <p className="text-sm text-muted leading-relaxed mb-4">{data.description}</p>}

        {data.prices?.length > 0 && (
          <div className="card p-3 mb-4">
            <div className="text-xs text-muted mb-2">{t('session.prices')}</div>
            <div className="flex flex-wrap gap-2">
              {data.prices.map((p) => (
                <span key={p.sector_name} className="px-3 py-1.5 rounded-full bg-bg-elev text-sm">
                  {p.sector_name}: <b>{formatUZS(p.price)}</b>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="fixed bottom-0 inset-x-0 p-4 bg-bg border-t border-line">
        <button className="btn-primary" onClick={() => navigate(`/session/${id}/seats`)}>
          {t('session.chooseSeats')}
        </button>
      </div>
    </div>
  );
}
